/**
 * @file     ListingIcons.tsx
 * @purpose  The one icon set for listing facts, highlights, amenities and
 *           travel rows: outline glyphs on a 24 grid at a 1.8 stroke, drawn in
 *           currentColor and always decorative (the label stays visible beside
 *           them). The metaphors are the ones the pre-redesign spec cards and
 *           highlight pills used; the resolvers map listingSpecs icon keys,
 *           fact labels and highlight text onto them.
 */

const P = {
  bed: 'M3 9v10M3 13h18M21 9v10M7 13V9a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4',
  bath: 'M4 12h16M4 12v7h16v-7M4 12V7a3 3 0 0 1 3-3h1a2 2 0 0 1 2 2M7 19l-1 2M17 19l1 2',
  area: 'M4 4h16v16H4zM4 9h3M9 4v3M20 15h-3M15 20v-3',
  lot: 'M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4',
  calendar: 'M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM4 10h16M8 3v4M16 3v4',
  paw: 'M12 12.5c-2.8 0-5 2.4-5 4.6 0 1.6 1.3 2.9 2.9 2.9.8 0 1.4-.3 2.1-.3s1.3.3 2.1.3c1.6 0 2.9-1.3 2.9-2.9 0-2.2-2.2-4.6-5-4.6zM7 9.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM11 6a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM16 6a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM20 9.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z',
  stairs: 'M3 20h5v-4h4v-4h4V8h5',
  car: 'M3 13l2-5.5A2 2 0 0 1 6.9 6h10.2a2 2 0 0 1 1.9 1.5L21 13v4H3zM3 13h18M5 17v2M19 17v2M7 15h.01M17 15h.01',
  strata: 'M3 10h18L12 4zM5 10v10M9.5 10v10M14.5 10v10M19 10v10M3 20h18',
  tax: 'M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21zM9 8h6M9 12h6M9 16h3',
  house: 'M4 11l8-7 8 7M6 9.5V20h12V9.5M10 20v-5h4v5',
  condo: 'M5 21V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v17M15 9h3a1 1 0 0 1 1 1v11M3 21h18M8 7h1M11 7h1M8 11h1M11 11h1M8 15h1M11 15h1',
  townhouse: 'M2 12l5-5 5 5 5-5 5 5M4 10.5V20h16v-9.5M12 12v8M7 20v-4M17 20v-4',
  duplex: 'M3 11l9-7 9 7M5 9.5V20h14V9.5M12 4v16',
  land: 'M3 19l6-8 4 5 3-3.5 5 6.5zM3 19h18',
  manufactured: 'M4 11l8-7 8 7M6 9.5V20h12V9.5M16 6.5V4h2v4.3M10 20v-5h4v5',
  key: 'M10.8 12.2 20 3M17 6l2.5 2.5M14.5 8.5 17 11M11 15a4 4 0 1 1-8 0 4 4 0 0 1 8 0z',
  multiplex: 'M3 21h18M5 21V8l7-5 7 5v13M9 21v-4h6v4M9 11h1M14 11h1',
  basement: 'M4 10l8-6 8 6M6 8.5V20h12V8.5M6 15h12',
  garage: 'M3 20V9l9-5 9 5v11M7 20v-8h10v8M7 15h10M7 18h10',
  carport: 'M3 9l9-5 9 5M5 8v12M19 8v12M8.5 14h7l1 3h-9z',
  flame: 'M12 21c3.3 0 6-2.5 6-5.8 0-3.7-2.8-5.7-4-9.2-1.8 1.6-2.5 3.6-2.3 5.2-.9-.8-1.7-1.7-1.9-3.2C7.6 9.8 6 12.3 6 15.2 6 18.5 8.7 21 12 21z',
  pin: 'M12 21s-7-5.6-7-11a7 7 0 1 1 14 0c0 5.4-7 11-7 11zM14.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z',
  style: 'M4 11l8-7 8 7M6 9.5V20h12V9.5M13.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z',
  ruler: 'M3 16.5 16.5 3 21 7.5 7.5 21zM7 13l2 2M10 10l2 2M13 7l2 2',
  document: 'M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7zM14 3v4h4M9 13h6M9 17h6',
  door: 'M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M3 21h18M14.5 12h.01',
  gym: 'M6.5 6.5v11M17.5 6.5v11M3.5 9v6M20.5 9v6M6.5 12h11',
  elevator: 'M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM9.5 10 12 7.5l2.5 2.5M9.5 14l2.5 2.5 2.5-2.5',
  laundry: 'M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM5 7h14M8 5h.01M11 5h.01M16 14a4 4 0 1 1-8 0 4 4 0 0 1 8 0z',
  snowflake: 'M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9M9.5 4.5 12 6l2.5-1.5M9.5 19.5 12 18l2.5 1.5',
  thermometer: 'M10 13.5V5a2 2 0 1 1 4 0v8.5a4 4 0 1 1-4 0zM12 17v-6',
  person: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM4 21a8 8 0 0 1 16 0',
  people: 'M12.5 7.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0zM2.5 20a6.5 6.5 0 0 1 13 0M16 4.3a3.5 3.5 0 0 1 0 6.4M18 14.2a6.5 6.5 0 0 1 3.5 5.8',
  droplet: 'M12 3s6 6.6 6 11a6 6 0 0 1-12 0c0-4.4 6-11 6-11z',
  sofa: 'M5 11V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3M3 13a2 2 0 0 1 4 0v2h10v-2a2 2 0 1 1 4 0v5H3zM5 18v2M19 18v2',
  box: 'M3 7.5 12 3l9 4.5v9L12 21l-9-4.5zM3 7.5l9 4.5 9-4.5M12 12v9',
  spray: 'M8 3s3 3.4 3 5.5a3 3 0 0 1-6 0C5 6.4 8 3 8 3zM16 11s3 3.4 3 5.5a3 3 0 0 1-6 0c0-2.1 3-5.5 3-5.5z',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  waves: 'M3 8c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0M3 13c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0M3 18c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0',
  mountain: 'M3 20 9.5 8l4 7 2.5-4 5 9z',
  city: 'M3 21V10h5v11M8 21V5h7v16M15 21v-8h6v8M2 21h20',
  tree: 'M12 3 6 11h3l-4 6h14l-4-6h3zM12 17v4',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
  deck: 'M3 11l9-7 9 7M3 15h18M5 15v6M19 15v6M9 15v6M15 15v6',
  balcony: 'M7 12V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v7M3 12h18M3 21h18M5 12v9M9 12v9M15 12v9M19 12v9',
  patio: 'M3 10 12 4l9 6zM12 10v11M8 21h8M7 15h10',
  bike: 'M9 17.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0zM22 17.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0zM5.5 17.5h6.5l-3-8M12 17.5l4.5-8H9M16.5 9.5l2 8M15 6.5h3M9 9.5H7.5',
  hotTub: 'M3 12h18v5a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3zM8 9V7.5M12 9V6M16 9V7.5',
  steam: 'M7.5 20c1.5-2 0-4 1.5-6s1.5-4 0-6M12 20c1.5-2 0-4 1.5-6s1.5-4 0-6M16.5 20c1.5-2 0-4 1.5-6s1.5-4 0-6',
  sauna: 'M4 14h16v3a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3zM8 4v6M12 3v7M16 4v6',
  bolt: 'M13 3 6 13h5l-1 8 7-10h-5z',
  grid: 'M4 4h16v16H4zM4 12h16M12 4v16',
  ceiling: 'M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4',
  anchor: 'M14.5 5.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zM12 8v13M5 13a7 7 0 0 0 14 0M8 11.5h8',
  flag: 'M8 21V3l9 4-9 4M5 21h6',
  walk: 'M14.5 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM10 21l2-6 2.5 2.5V21M8 12.5l2-4 3.5-.5 2 3.5 2.5 1M12 15l1-6',
  transit: 'M6 3h12a2 2 0 0 1 2 2v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V5a2 2 0 0 1 2-2zM4 11h16M8 15h.01M16 15h.01M7 18l-1.5 3M17 18l1.5 3',
  school: 'M2 9l10-5 10 5-10 5zM6 11v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5M22 9v6',
  fridge: 'M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM5 10h14M8 6v2M8 13v3',
  roof: 'M2 13 12 5l10 8M5 11l7-5.5 7 5.5M5 11v9h14v-9',
  brick: 'M3 5h18v14H3zM3 9.7h18M3 14.3h18M9 5v4.7M15 5v4.7M6 9.7v4.6M12 9.7v4.6M18 9.7v4.6M9 14.3V19M15 14.3V19',
  text: 'M4 6h16M4 10h16M4 14h16M4 18h10',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 8h.01',
  chart: 'M4 20V10M10 20V4M16 20v-7M2 20h20',
} as const;

export type ListingIconName = keyof typeof P;

export interface ListingIconProps {
  name: ListingIconName;
  /** 18 in fact rows and section headers, 16 inline in pills and strips. */
  size?: 16 | 18 | 20;
}

export function ListingIcon({ name, size = 18 }: ListingIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-listing-icon={name}
      style={{ flexShrink: 0, display: 'block' }}
    >
      <path d={P[name]} />
    </svg>
  );
}

const TYPE_ICON: Record<string, ListingIconName> = {
  condo: 'condo',
  townhouse: 'townhouse',
  house: 'house',
  duplex: 'duplex',
  land: 'land',
  manufactured: 'manufactured',
  rent: 'key',
  income: 'multiplex',
};

const SPEC_ICON: Record<string, ListingIconName> = {
  bed: 'bed',
  bath: 'bath',
  sqft: 'area',
  lot: 'lot',
  cal: 'calendar',
  pets: 'paw',
  stairs: 'stairs',
  car: 'car',
  strata: 'strata',
  tax: 'tax',
  basement: 'basement',
  garage: 'garage',
  carport: 'carport',
  fireplace: 'flame',
  mappin: 'pin',
  style: 'style',
  gym: 'gym',
  elev: 'elevator',
  wash: 'laundry',
  ac: 'snowflake',
  fire: 'flame',
  person: 'person',
  gas: 'flame',
  droplet: 'droplet',
  sofa: 'sofa',
  box: 'box',
  spray: 'spray',
  check: 'check',
};

/** A listingSpecs `iconKey` (`bed`, `strata`, `type:condo`, ...) as a set icon. */
export function specIconName(iconKey: string): ListingIconName {
  if (iconKey.startsWith('type:')) return TYPE_ICON[iconKey.slice(5)] ?? 'house';
  return SPEC_ICON[iconKey] ?? 'check';
}

const FACT_ICON: Record<string, ListingIconName> = {
  Listed: 'calendar',
  Style: 'style',
  Basement: 'basement',
  Structure: 'house',
  Frontage: 'ruler',
  PID: 'document',
  'Common interest': 'strata',
  Levels: 'stairs',
  Entry: 'door',
  'Main floor': 'area',
  'Tax year': 'calendar',
};

/** Facts that carry no spec key (the details strip, the additional facts). */
export function factIconName(label: string): ListingIconName | null {
  return FACT_ICON[label] ?? null;
}

const GROUP_ICON: Record<string, ListingIconName> = {
  Appliances: 'fridge',
  Heating: 'thermometer',
  Cooling: 'snowflake',
  Flooring: 'grid',
  Construction: 'brick',
  Roof: 'roof',
  Parking: 'car',
  Lot: 'lot',
  'Interior features': 'sofa',
  Laundry: 'laundry',
  Community: 'people',
};

/** The labelled inclusion lists (Appliances, Heating, ...). */
export function featureGroupIconName(label: string): ListingIconName {
  return GROUP_ICON[label] ?? 'check';
}

/** A highlight or amenity pill, matched on its text the way the old pills were. */
export function highlightIconName(label: string): ListingIconName {
  const l = label.toLowerCase();
  if (l.includes('ocean') || l.includes('water view')) return 'waves';
  if (l.includes('mountain')) return 'mountain';
  if (l.includes('city')) return 'city';
  if (l.includes('greenbelt') || l.includes('park view')) return 'tree';
  if (l.includes('view')) return 'eye';
  if (l.includes('rooftop') || l.includes('deck')) return 'deck';
  if (l.includes('balcony')) return 'balcony';
  if (l.includes('patio') || l.includes('yard')) return 'patio';
  if (l.includes('bike')) return 'bike';
  if (l.includes('gym') || l.includes('exercise') || l.includes('fitness') || l.includes('recreation')) return 'gym';
  if (l.includes('pool')) return 'waves';
  if (l.includes('hot tub') || l.includes('jacuzzi')) return 'hotTub';
  if (l.includes('steam')) return 'steam';
  if (l.includes('sauna')) return 'sauna';
  if (/\bev\b/.test(l) || l.includes('charging') || l.includes('charger')) return 'bolt';
  if (l.includes('garage')) return 'garage';
  if (l.includes('parking') || l.includes('carport')) return 'car';
  if (l.includes('car wash')) return 'spray';
  if (l.includes('storage') || l.includes('locker')) return 'box';
  if (l.includes('laundry')) return 'laundry';
  if (l.includes('elevator')) return 'elevator';
  if (l.includes('fireplace') || l.includes('gas')) return 'flame';
  if (l.includes('air conditioning') || l.includes('a/c') || l.includes('cooling')) return 'snowflake';
  if (l.includes('radiant') || l.includes('geothermal') || l.includes('heat')) return 'thermometer';
  if (l.includes('hot water')) return 'droplet';
  if (l.includes('hardwood') || l.includes('floor')) return 'grid';
  if (l.includes('ceiling')) return 'ceiling';
  if (l.includes('waterfront') || l.includes('ski')) return 'anchor';
  if (l.includes('golf')) return 'flag';
  if (
    l.includes('meeting') || l.includes('party room') || l.includes('clubhouse') || l.includes('club house') ||
    l.includes('amenity room') || l.includes('common room') || l.includes('lounge')
  ) return 'sofa';
  if (l.includes('concierge') || l.includes('caretaker') || l.includes('security')) return 'person';
  if (l.includes('guest suite')) return 'bed';
  if (l.includes('trail')) return 'walk';
  return 'check';
}
